import { useEffect, useRef, useState } from "react";
import { Menu, X, ChevronDown, User } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { DOCM_LOGO, NAV_PRIMARY, NAV_SUBMENUS } from "@/data/docm";
import { loadIdentity, clearIdentity } from "@/lib/identity";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // key of submenu open on desktop
  const [mobileSubOpen, setMobileSubOpen] = useState(null); // key of submenu open on mobile
  const [identity, setIdentity] = useState(loadIdentity);
  const [identityOpen, setIdentityOpen] = useState(false);
  const headerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Keep the identity chip in sync across tabs + the EmailGate dispatch.
  useEffect(() => {
    const onChange = () => setIdentity(loadIdentity());
    window.addEventListener("docm:identity-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("docm:identity-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  // Hover-with-grace-period helpers — keeps the submenu open while the cursor
  // travels from the trigger button down to the menu card.
  const openSubmenu = (key) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpenMenu(key);
  };
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpenMenu(null), 140);
  };
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close desktop dropdown on outside click / escape
  useEffect(() => {
    const onDocClick = (e) => {
      if (!headerRef.current?.contains(e.target)) setOpenMenu(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Close any open menus whenever the route changes. We use a ref-tracked
  // comparison so we never call setState unconditionally inside the effect.
  const lastPathRef = useRef(location.pathname + location.hash);
  useEffect(() => {
    const current = location.pathname + location.hash;
    if (lastPathRef.current !== current) {
      lastPathRef.current = current;
      setOpenMenu(null);
      setMobileSubOpen(null);
      setMobileOpen(false);
    }
  }, [location.pathname, location.hash]);

  const goAnchor = (anchor) => {
    if (location.pathname !== "/") {
      navigate({ pathname: "/", hash: `#${anchor.replace("#", "")}` });
    } else {
      const el = document.getElementById(anchor.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleNav = (link) => {
    setMobileOpen(false);
    setOpenMenu(null);
    // Defensive guard (2026-07-21): a nav / submenu item missing `type`
    // used to be a silent no-op (bug: NAV_SUBMENUS.join dropdown was
    // unresponsive). Warn loudly so future edits to the config fail
    // fast in the browser console rather than in QA.
    if (!link?.type) {
      // Warn loudly so future edits to the config fail fast in the
      // browser console rather than in QA.
      console.warn("[Navbar] nav item is missing `type` — click will be ignored", link);
      return;
    }
    if (link.type === "route") {
      navigate(link.href);
    } else if (link.type === "anchor-home") {
      goAnchor(link.href.replace("/", ""));
    } else if (link.type === "anchor") {
      goAnchor(link.href);
    }
  };

  const handleCta = (target) => {
    setMobileOpen(false);
    setOpenMenu(null);
    if (target === "sign") {
      // Bug #8 fix (2026-07-08): a returning signed user clicking "Sign the
      // Declaration" was landing on a stale "success" screen at
      // /one-conscious-act (because the wizard's initial stage resolved to
      // "success" when a signatureRef exists on localStorage). We now
      // route already-signed visitors to the home Sign section where the
      // "Welcome back, you already signed" banner + Random Act CTAs appear.
      const ident = loadIdentity();
      if (ident?.signatureRef) {
        if (location.pathname === "/") {
          goAnchor("#sign");
        } else {
          navigate("/#sign");
        }
        return;
      }
      navigate("/one-conscious-act");
    } else {
      goAnchor(`#${target}`);
    }
  };

  const slug = (s) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const isLinkActive = (link) =>
    link.type === "route" &&
    ((link.href === "/" && location.pathname === "/") ||
      (link.href !== "/" && location.pathname.startsWith(link.href)));

  return (
    <header
      ref={headerRef}
      data-testid="docm-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 glass ${
        scrolled ? "py-2.5" : "py-3.5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center gap-4">
        <Link
          to="/"
          data-testid="docm-logo-link"
          className="flex items-center gap-3 group shrink-0 mr-2"
          aria-label="DOCM home"
          onClick={(e) => {
            setMobileOpen(false);
            // Bug fix 2026-07-08: if already on the home page, the router
            // does nothing and the user stays scrolled to wherever they were.
            // Explicitly scroll to top so tapping the DOCM logo always
            // returns to the hero — the natural expectation for a
            // landing-page logo click.
            if (location.pathname === "/") {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          <img
            src={DOCM_LOGO}
            alt="DOCM"
            className="h-10 md:h-11 w-auto transition-all duration-500 group-hover:scale-[1.03]"
          />
        </Link>

        {/* Primary nav — pushed to the right with `ml-auto` so it hugs
            the right-side actions (Sign In / Sign the Declaration) with a
            single natural gap-4 between the two clusters. 2026-07-26 fix:
            eliminates the ~180px empty band that used to sit between
            "Our Founder" and "Sign In". */}
        <nav className="hidden xl:flex items-center gap-4 ml-auto">
          {NAV_PRIMARY.map((link) => {
            const submenu = link.hasSubmenu && NAV_SUBMENUS[link.hasSubmenu];
            const isActive = isLinkActive(link);
            const baseColor = link.highlight
              ? "text-docm-terracotta hover:text-docm-moss"
              : isActive
              ? "text-docm-moss"
              : "text-docm-ink hover:text-docm-moss";

            if (submenu) {
              const isOpen = openMenu === link.hasSubmenu;
              return (
                <div
                  key={link.label}
                  className="relative flex items-center"
                  onMouseEnter={() => openSubmenu(link.hasSubmenu)}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    data-testid={`nav-link-${slug(link.label)}`}
                    onClick={() => handleNav(link)}
                    onFocus={() => openSubmenu(link.hasSubmenu)}
                    aria-expanded={isOpen}
                    aria-haspopup="menu"
                    className={`relative inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] uppercase tracking-[0.14em] font-semibold transition-colors duration-300 ${baseColor}`}
                  >
                    {link.label}
                    <ChevronDown
                      size={12}
                      className={`mt-px transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                    {isActive && (
                      <span className="absolute -bottom-2 left-0 right-4 h-[2.5px] rounded-full bg-docm-moss" />
                    )}
                  </button>
                  {isOpen && (
                    <div
                      data-testid={`nav-submenu-${link.hasSubmenu}`}
                      role="menu"
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-[60]"
                      onMouseEnter={() => openSubmenu(link.hasSubmenu)}
                      onMouseLeave={scheduleClose}
                    >
                      <div className="min-w-[300px] rounded-2xl bg-docm-cream border border-docm-border shadow-[0_30px_60px_-30px_rgba(28,32,30,0.45)] py-2.5 overflow-hidden">
                        {submenu.map((s) => (
                          <button
                            key={s.label}
                            role="menuitem"
                            data-testid={`nav-submenu-item-${slug(s.label)}`}
                            onClick={() => handleNav(s)}
                            className="w-full text-left px-4 py-3 group hover:bg-docm-linen/60 transition-colors border-b border-docm-border/40 last:border-b-0"
                          >
                            <div className="text-[13px] uppercase tracking-[0.14em] font-semibold text-docm-ink group-hover:text-docm-moss transition-colors">
                              {s.label}
                            </div>
                            {s.desc && (
                              <div className="mt-0.5 text-[11.5px] text-docm-inkSoft normal-case tracking-normal font-normal">
                                {s.desc}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Stacked-badge variant — "11.11" on top, "GLOBAL EVENT" below,
            // rendered as ONE tight pill (Sathish's Proposed Changes #14).
            if (link.stackedBadge && link.badge) {
              return (
                <button
                  key={link.label}
                  data-testid={`nav-link-${slug(link.label)}`}
                  onClick={() => handleNav(link)}
                  className={`relative inline-flex flex-col items-center justify-center whitespace-nowrap rounded-lg border transition-colors duration-300 px-3 py-1 leading-none ${
                    isActive
                      ? "border-docm-terracotta bg-docm-terracotta/15 text-docm-terracotta"
                      : "border-docm-terracotta/60 bg-docm-terracotta/8 text-docm-terracotta hover:bg-docm-terracotta hover:text-docm-cream"
                  }`}
                >
                  <span className="text-[13px] font-bold tracking-[0.05em] leading-tight">{link.label.replace("·", ".")}</span>
                  <span
                    data-testid={`nav-badge-${slug(link.label)}`}
                    className="text-[8.5px] font-bold uppercase tracking-[0.18em] leading-tight mt-0.5"
                  >
                    {link.badge}
                  </span>
                </button>
              );
            }

            return (
              <button
                key={link.label}
                data-testid={`nav-link-${slug(link.label)}`}
                onClick={() => handleNav(link)}
                className={`relative inline-flex items-center gap-2 whitespace-nowrap text-[12.5px] uppercase tracking-[0.14em] font-semibold transition-colors duration-300 ${baseColor}`}
              >
                <span>{link.displayLabel || link.label}</span>
                {link.badge && (
                  <span
                    data-testid={`nav-badge-${slug(link.label)}`}
                    className="px-1.5 py-[2px] rounded-sm bg-docm-terracotta text-docm-cream text-[8.5px] font-bold tracking-[0.14em] leading-none shadow-[0_4px_10px_-4px_rgba(155,74,58,0.55)] whitespace-nowrap"
                  >
                    {link.badge}
                  </span>
                )}
                {link.highlight && !link.badge && (
                  <span className="absolute -top-1 -right-3 w-1.5 h-1.5 rounded-full bg-docm-terracotta animate-pulse" />
                )}
                {isActive && (
                  <span
                    className={`absolute -bottom-2 left-0 right-0 h-[2.5px] rounded-full ${
                      link.highlight ? "bg-docm-terracotta" : "bg-docm-moss"
                    }`}
                  />
                )}
              </button>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          {identity?.email ? (
            <div className="relative">
              <button
                type="button"
                data-testid="navbar-identity-chip"
                onClick={() => setIdentityOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-docm-moss/10 border border-docm-moss/30 text-docm-moss hover:bg-docm-moss/15 transition-colors max-w-[160px]"
                aria-haspopup="menu"
                aria-expanded={identityOpen}
                title={identity.email}
              >
                <User size={13} />
                <span className="text-[11.5px] font-semibold tracking-[0.06em] truncate">
                  {(identity.name || "").split(" ")[0] || identity.email.split("@")[0]}
                </span>
                <ChevronDown size={11} className={`transition-transform ${identityOpen ? "rotate-180" : ""}`} />
              </button>
              {identityOpen && (
                <div
                  data-testid="navbar-identity-menu"
                  className="absolute right-0 top-full mt-2 min-w-[240px] rounded-2xl bg-docm-cream border border-docm-border shadow-[0_30px_60px_-30px_rgba(28,32,30,0.45)] py-2.5 z-[60]"
                  onMouseLeave={() => setIdentityOpen(false)}
                >
                  <div className="px-4 pb-2 border-b border-docm-border/60">
                    <div className="text-[10.5px] uppercase tracking-[0.16em] text-docm-inkSoft font-semibold">Signed in as</div>
                    <div className="mt-0.5 text-[13px] font-semibold text-docm-ink truncate">{identity.email}</div>
                    {identity.declaredAt && (
                      <div data-testid="navbar-declared-on" className="mt-1.5 text-[11px] text-docm-moss">
                        Declared on {(() => { try { return new Date(identity.declaredAt).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } })()}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid="navbar-identity-switch"
                    onClick={() => {
                      clearIdentity();
                      setIdentity(null);
                      setIdentityOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-[12.5px] uppercase tracking-[0.14em] font-semibold text-docm-terracotta hover:bg-docm-linen/60 transition-colors"
                  >
                    Not you? Switch
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              data-testid="navbar-signin-cta"
              onClick={() => {
                // Force-open the EmailGate so returning declared visitors can self-identify.
                window.dispatchEvent(new CustomEvent("docm:request-identity", {
                  detail: { reason: "Welcome back — enter your email to continue your journey", force: true },
                }));
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-docm-border bg-docm-paper text-docm-ink hover:border-docm-moss/50 hover:text-docm-moss transition-colors text-[11.5px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap"
              title="Already declared? Sign in"
            >
              <User size={12} /> Sign in
            </button>
          )}
          <button
            data-testid="navbar-sign-cta"
            onClick={() => handleCta("sign")}
            className="inline-flex items-center justify-center rounded-full font-sans font-medium px-4 py-2 text-[12.5px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 bg-docm-moss text-docm-cream hover:bg-docm-mossHover whitespace-nowrap"
          >
            Sign the Declaration
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden p-2 text-docm-ink"
          onClick={() => setMobileOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="xl:hidden glass border-t border-docm-border/60 px-5 py-5 space-y-1 max-h-[80vh] overflow-y-auto">
          {NAV_PRIMARY.map((link) => {
            const submenu = link.hasSubmenu && NAV_SUBMENUS[link.hasSubmenu];
            if (submenu) {
              const isOpen = mobileSubOpen === link.hasSubmenu;
              return (
                <div key={link.label} className="border-b border-docm-border/40">
                  <button
                    data-testid={`mobile-nav-${slug(link.label)}`}
                    onClick={() => setMobileSubOpen(isOpen ? null : link.hasSubmenu)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between text-left text-sm font-medium py-3 text-docm-ink"
                  >
                    <span>{link.label}</span>
                    <ChevronDown
                      size={16}
                      className={`text-docm-ink/60 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div
                      data-testid={`mobile-nav-submenu-${link.hasSubmenu}`}
                      className="pl-3 pb-2 space-y-0.5"
                    >
                      {submenu.map((s) => (
                        <button
                          key={s.label}
                          data-testid={`mobile-nav-submenu-item-${slug(s.label)}`}
                          onClick={() => handleNav(s)}
                          className="block w-full text-left text-[13px] font-medium py-2.5 text-docm-inkSoft hover:text-docm-moss"
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button
                key={link.label}
                data-testid={`mobile-nav-${slug(link.label)}`}
                onClick={() => handleNav(link)}
                className={`block w-full text-left text-sm font-medium py-3 pl-3 -ml-3 pr-3 border-b border-docm-border/40 transition-colors duration-200 ${
                  isLinkActive(link)
                    ? "text-docm-moss font-semibold bg-docm-moss/8 border-l-4 border-l-docm-moss"
                    : link.highlight
                    ? "text-docm-terracotta"
                    : "text-docm-ink"
                }`}
                data-active={isLinkActive(link) ? "true" : undefined}
              >
                {link.label}
              </button>
            );
          })}
          <div className="pt-4 flex flex-col gap-3">
            <button
              data-testid="mobile-sign-cta"
              onClick={() => handleCta("sign")}
              className="btn-primary w-full"
            >
              Sign the Declaration
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
